import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import kycService from "../../services/kycService";

const KYCStatusWidget = () => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkKYCStatus();
  }, []);

  const checkKYCStatus = async () => {
    try {
      const response = await kycService.checkKYCStatus();
      // Normalize empty string to null for consistent handling
      const status = response.kyc_answer === "" ? null : response.kyc_answer;
      setKycStatus(status);
    } catch (error) {
      console.error("Error checking KYC status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    if (loading) {
      return {
        icon: (
          <svg
            className="animate-spin h-10 w-10 text-indigo-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ),
        title: "Checking Status...",
        description: "Verifying your identity",
        bgColor: "bg-white dark:bg-gray-800",
        borderColor: "border-gray-300 dark:border-gray-600",
        iconBg: "bg-indigo-100",
        textColor: " dark:text-white",
        badge: null,
        button: null,
      };
    }

    if (kycStatus === "GREEN") {
      return {
        icon: (
          <svg
            className="h-10 w-10 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        ),
        title: "Identity Verified",
        description: "Your account has full access to all features",
        bgColor:
          "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900 dark:to-green-900",
        borderColor: "border-emerald-400 dark:border-emerald-600",
        iconBg: "bg-gradient-to-br from-emerald-500 to-green-600",
        textColor: " dark:text-white",
        badge: (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white shadow-sm">
            ✓ Verified
          </span>
        ),
        button: null,
      };
    }

    if (kycStatus === "RED") {
      return {
        icon: (
          <svg
            className="h-10 w-10 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        ),
        title: "Verification Failed",
        description:
          "Your verification was not approved. Contact support for assistance.",
        bgColor:
          "bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900 dark:to-rose-900",
        borderColor: "border-red-400 dark:border-red-600",
        textColor: " dark:text-white",
        badge: (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white shadow-sm">
            ✗ Rejected
          </span>
        ),
        button: {
          text: "Contact Support",
          action: () => (window.location.href = "mailto:support@P2P1.com"),
          style:
            "bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white shadow-lg hover:shadow-xl",
        },
      };
    }

    // Not verified - Clean blue/indigo theme
    return {
      icon: (
        <img
          src="/verify-your-identity.svg"
          alt="Verify Your Identity"
          className="w-10 h-10 object-contain"
        />
      ),
      title: "Verify Your Identity",
      description: "Complete verification to unlock all platform features",
      bgColor: "bg-gray-800 dark:bg-gray-800",
      borderColor: "!border-[#2A356D] dark:border-indigo-600",
      iconBg: "!bg-black !text-white dark:bg-black",
      textColor: "!text-white dark:text-white",
      badge: (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold !bg-[#0E1696] !text-white shadow-sm animate-pulse">
          ⚠ Action Required
        </span>
      ),
      button: {
        text: "Start Verification",
        action: () => navigate("/kyc"),
        style:
          "!bg-[#0E1696] !text-white hover:from-indigo-700 hover:to-purple-800 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all",
      },
    };
  };

  const status = getStatusDisplay();

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 ${status.borderColor} ${status.bgColor} shadow-xl hover:shadow-2xl transition-all duration-300`}
    >
      <div className="relative p-6">
        {/* Main Content */}
        <div className="flex items-start space-x-5">
          {/* Icon with gradient background */}
          <div className="flex-shrink-0 p-4 rounded-2xl ">{status.icon}</div>

          <div className="flex-1 min-w-0">
            {/* Title and Badge */}
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className={`text-2xl font-bold ${status.textColor}`}>
                {status.title}
              </h3>
              {status.badge && status.badge}
            </div>

            {/* Description */}
            <p
              className={`text-base ${status.textColor} font-medium leading-relaxed mb-4`}
            >
              {status.description}
            </p>

            {/* Action button - inline with content */}
            {status.button && (
              <button
                onClick={status.button.action}
                className={`inline-flex items-center justify-center px-6 py-3 text-base font-bold rounded-xl ${status.button.style} focus:outline-none focus:ring-4 focus:ring-indigo-300`}
              >
                {status.button.text}
                <svg
                  className="ml-2 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Info banner for unverified users */}
        {!kycStatus && (
          <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border-2 border-blue-400 dark:border-blue-400">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-indigo-600 dark:text-indigo-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm !text-white font-medium leading-relaxed">
                  <span className="font-bold !text-white">
                    Optional for now:{" "}
                  </span>{" "}
                  <p className="text-sm !text-white">
                    KYC may be required for trading later. Complete it early to
                    avoid delays and unlock full platform access!
                  </p>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success celebration for verified users */}
        {kycStatus === "GREEN" && (
          <div className="mt-6 p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl border-2 border-emerald-300 dark:border-emerald-600">
            <div className="flex items-center">
              <span className="text-3xl mr-3">🎉</span>
              <div className="flex-1">
                <p className="text-base  dark:text-white font-bold">
                  You're all set!
                </p>
                <p className="text-sm text-white dark:text-white">
                  Enjoy full platform access
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCStatusWidget;
